import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import LandingHero from "../components/landing/LandingHero"
import InstantFilingSection from "../components/landing/InstantFilingSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_SERVICE_CARDS = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand across the European Union. File an EU trademark online with professional representation before the EUIPO, without the complexity of managing the European filing process yourself.",
    path: "/trademark",
    textKey: "home.services.trademark.text",
    titleKey: "home.services.trademark.title"
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect your design in Europe, simply and efficiently. File your EU design online and let our European Patent Attorneys take care of the official filing and representation before the EUIPO.",
    path: "/design",
    textKey: "home.services.design.text",
    titleKey: "home.services.design.title"
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "File your European patent application online. Provide your application information and documents online. We prepare and submit your application and professionally represent you before the European Patent Office.",
    path: "/patent",
    textKey: "home.services.patent.text",
    titleKey: "home.services.patent.title"
  }
]

export default function StartPage() {
  const location = useLocation()
  const [infoMessage, setInfoMessage] = useState("")
  const site = useSiteContent("home")

  const serviceCards = DEFAULT_SERVICE_CARDS.map(({ textKey, titleKey, ...card }) => ({
    ...card,
    text: site.get(textKey, card.text),
    title: site.get(titleKey, card.title)
  }))

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const message = params.get("message")

    if (message === "not-logged-in") {
      setInfoMessage("Please log in first or register if you don't have an account yet.")

      const newUrl = window.location.origin + location.pathname
      window.history.replaceState({}, "", newUrl)
    }
  }, [location.search, location.pathname])

  return (
    <div className="flex min-h-screen flex-col">
      {infoMessage && (
        <div className="bg-red-50 py-2 text-center text-sm text-red-600">{infoMessage}</div>
      )}

      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <LandingHero
        heading={site.get("home.hero.heading", "EUROPEAN IP FILING, MADE SIMPLE")}
        intro={site.get(
          "home.hero.intro",
          "File your patent, trademark or design in Europe through one simple " +
            "online process. xpress-ip combines the convenience of digital filing " +
            "with professional representation before the European IP offices in " +
            "cooperation with experienced European Patent Attorneys at DANTZ IP & LAW."
        )}
        tagline={site.get("home.hero.tagline", "Simple. Fast. Professional. Predictable.")}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("home.hero.heading", value)}
        onSaveIntro={(value) => site.save("home.hero.intro", value)}
        onSaveTagline={(value) => site.save("home.hero.tagline", value)}
      />

      <InstantFilingSection
        heading={site.get("home.services.heading", "THREE MAIN SERVICES")}
        types={serviceCards}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("home.services.heading", value)}
        onSaveCardText={(index, value) =>
          site.save(DEFAULT_SERVICE_CARDS[index].textKey, value)
        }
        onSaveCardTitle={(index, value) =>
          site.save(DEFAULT_SERVICE_CARDS[index].titleKey, value)
        }
      />

      <LandingTextSection
        heading={site.get(
          "home.steps.heading",
          "YOUR EUROPEAN IP FILING IN A FEW SIMPLE STEPS"
        )}
        divider={false}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("home.steps.heading", value)}
        body={[
          site.get(
            "home.steps.body.0",
            "You provide the information and documents online. We take care of the filing process."
          ),
          site.get(
            "home.steps.body.1",
            "From preparing the application documents to submitting the application, paying the official fees and managing your deadlines, xpress-ip provides the essential services you need for your European IP filing."
          )
        ]}
        onSaveBody={(index, value) => site.save(`home.steps.body.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("home.why.heading", "WHY XPRESS-IP?")}
        subheading={site.get(
          "home.why.subheading",
          "Professional European representation. Without the complexity."
        )}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("home.why.heading", value)}
        onSaveSubheading={(value) => site.save("home.why.subheading", value)}
        body={[
          site.get(
            "home.why.body.0",
            "File your patent, trademark or design in Europe through one simple streamlined online process with complete cost control. xpress-ip combines the convenience of digital filing with professional representation before the European IP offices in cooperation with experienced European Patent Attorneys at DANTZ IP & LAW."
          ),
          site.get(
            "home.why.body.1",
            "Your application is handled and represented before the relevant European IP office in cooperation with DANTZ IP & LAW, an established European patent law firm based in Germany."
          ),
          site.get(
            "home.why.body.2",
            "You benefit from a streamlined digital process combined with professional European IP representation. Data Security - Your data safe. It is stored on servers in Switzerland only."
          )
        ]}
        onSaveBody={(index, value) => site.save(`home.why.body.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("home.cost.heading", "COST CONTROL")}
        subheading={site.get("home.cost.subheading", "Know your costs. Control your process.")}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("home.cost.heading", value)}
        onSaveSubheading={(value) => site.save("home.cost.subheading", value)}
        body={[
          site.get(
            "home.cost.body.0",
            "xpress-ip is designed to keep European IP filing simple, transparent and predictable."
          ),
          site.get(
            "home.cost.body.1",
            "Official fees are paid after receipt of your payment. No additional cost-generating services are performed automatically."
          ),
          site.get("home.cost.body.2", "You decide which services you need.")
        ]}
        onSaveBody={(index, value) => site.save(`home.cost.body.${index}`, value)}
      />

      <Footer
        editable={site.editMode}
        email={site.get("home.footer.email", "info@xpress-ip.com")}
        tagline={site.get("home.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("home.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("home.footer.email", value)}
        onSaveTagline={(value) => site.save("home.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("home.footer.taglineSub", value)}
      />
    </div>
  )
}
